(() => {
  'use strict';

  const ROOT = '[data-svf-root]';
  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(pointer: fine)');

  class ShoppableVideoCarousel {
    constructor(root) {
      this.root = root;
      this.feed = root.querySelector('[data-svf-feed]');
      this.slides = [...root.querySelectorAll('[data-svf-slide]')];
      this.prev = root.querySelector('[data-svf-prev]');
      this.next = root.querySelector('[data-svf-next]');
      this.current = root.querySelector('[data-svf-current]');
      this.total = root.querySelector('[data-svf-total]');
      this.progress = root.querySelector('[data-svf-progress-fill]');

      this.backdrop = root.querySelector('[data-svf-sheet-backdrop]');
      this.sheet = root.querySelector('[data-svf-sheet]');
      this.sheetTitle = root.querySelector('[data-svf-sheet-title]');
      this.sheetPrice = root.querySelector('[data-svf-sheet-price]');
      this.sheetOptions = root.querySelector('[data-svf-sheet-options]');
      this.sheetCta = root.querySelector('[data-svf-sheet-cta]');

      this.toast = root.querySelector('[data-svf-toast]');
      this.toastText = root.querySelector('[data-svf-toast-text]');

      this.autoplay = root.dataset.autoplay === 'true';
      this.loop = root.dataset.loop === 'true';
      this.activeIndex = 0;
      this.sectionVisible = false;
      this.raf = 0;
      this.drag = null;
      this.suppressClick = false;
      this.suppressTimer = 0;
      this.toastTimer = 0;
      this.lastFocused = null;
      this.sheetState = null;
      this.productCache = new WeakMap();
      this.abort = new AbortController();
      this.sectionObserver = null;
      this.resizeObserver = null;
    }

    init() {
      if (!this.feed || !this.slides.length) return;

      const signal = this.abort.signal;

      this.feed.addEventListener('scroll', () => this.onScroll(), {
        passive: true,
        signal
      });

      this.feed.addEventListener('keydown', (event) => {
        if (event.target !== this.feed) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      }, { signal });

      this.root.addEventListener(
        'click',
        (event) => this.onClick(event),
        { signal }
      );

      this.feed.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;

          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        },
        {
          capture: true,
          signal
        }
      );

      if (finePointer.matches) {
        this.feed.addEventListener(
          'pointerdown',
          (event) => this.pointerDown(event),
          { signal }
        );

        this.feed.addEventListener(
          'pointermove',
          (event) => this.pointerMove(event),
          { signal }
        );

        this.feed.addEventListener(
          'pointerup',
          (event) => this.pointerUp(event),
          { signal }
        );

        this.feed.addEventListener(
          'pointercancel',
          (event) => this.pointerUp(event),
          { signal }
        );
      }

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.pauseAll();
          } else if (this.sectionVisible) {
            this.syncPlayback();
          }
        },
        { signal }
      );

      if (this.sheet) {
        this.sheet.addEventListener(
          'keydown',
          (event) => this.sheetKeydown(event),
          { signal }
        );
      }

      this.slides.forEach((slide) => {
        const poster = slide.querySelector('.svf-slide__poster');
        const video = slide.querySelector('[data-svf-video]');

        if (poster) {
          if (poster.complete) {
            slide.dataset.mediaReady = 'true';
          } else {
            poster.addEventListener(
              'load',
              () => {
                slide.dataset.mediaReady = 'true';
              },
              {
                once: true,
                signal
              }
            );
          }
        }

        if (!video) return;

        video.addEventListener(
          'loadeddata',
          () => {
            slide.dataset.mediaReady = 'true';
          },
          { signal }
        );

        video.addEventListener(
          'play',
          () => {
            slide.dataset.playing = 'true';
            slide.dataset.started = 'true';
            this.updateMute(slide, video);
          },
          { signal }
        );

        video.addEventListener(
          'pause',
          () => {
            slide.dataset.playing = 'false';
            this.updateMute(slide, video);
          },
          { signal }
        );

        video.addEventListener(
          'volumechange',
          () => {
            this.updateMute(slide, video);
          },
          { signal }
        );
      });

      if ('IntersectionObserver' in window) {
        this.sectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];

            this.sectionVisible =
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.12;

            if (this.sectionVisible) {
              this.syncPlayback();
            } else {
              this.pauseAll();
            }
          },
          {
            threshold: [0, 0.12, 0.3, 0.6]
          }
        );

        this.sectionObserver.observe(this.root);
      } else {
        this.sectionVisible = true;
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateActive(false);
          this.updateNav();
        });

        this.resizeObserver.observe(this.feed);
      } else {
        window.addEventListener(
          'resize',
          () => {
            this.updateActive(false);
            this.updateNav();
          },
          {
            passive: true,
            signal
          }
        );
      }

      this.updateActive(true);
      this.updateNav();
      this.updatePagination();
    }

    destroy() {
      this.abort.abort();

      this.sectionObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.raf) {
        cancelAnimationFrame(this.raf);
      }

      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }

      if (this.suppressTimer) {
        clearTimeout(this.suppressTimer);
      }

      this.pauseAll();
    }

    onScroll() {
      if (this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = 0;

        this.updateActive(false);
        this.updateNav();
        this.updatePagination();
      });
    }

    onClick(event) {
      if (event.target.closest('[data-svf-sheet-close]')) {
        this.closeSheet();
        return;
      }

      if (
        this.backdrop &&
        event.target === this.backdrop &&
        this.backdrop.dataset.open === 'true'
      ) {
        this.closeSheet();
        return;
      }

      const option = event.target.closest('[data-svf-option-value]');

      if (option && this.sheet?.contains(option)) {
        this.selectOption(option);
        return;
      }

      if (event.target.closest('[data-svf-sheet-cta]')) {
        this.addSelectedVariant();
        return;
      }

      if (event.target.closest('[data-svf-prev]')) {
        this.move(-1);
        return;
      }

      if (event.target.closest('[data-svf-next]')) {
        this.move(1);
        return;
      }

      const mute = event.target.closest('[data-svf-mute]');

      if (mute) {
        this.toggleMute(mute);
        return;
      }

      const add = event.target.closest('[data-svf-add-to-cart]');

      if (add) {
        this.handleAdd(add);
        return;
      }

      const video = event.target.closest('[data-svf-video]');

      if (video) {
        this.toggleVideo(video);
      }
    }

    pointerDown(event) {
      if (
        event.pointerType !== 'mouse' ||
        event.button !== 0
      ) {
        return;
      }

      if (
        event.target.closest(
          'a,button,input,select,textarea,[role="button"]'
        )
      ) {
        return;
      }

      this.drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        left: this.feed.scrollLeft,
        moved: false
      };

      this.feed.setPointerCapture?.(event.pointerId);
    }

    pointerMove(event) {
      if (
        !this.drag ||
        event.pointerId !== this.drag.id
      ) {
        return;
      }

      const dx = event.clientX - this.drag.x;
      const dy = event.clientY - this.drag.y;

      if (!this.drag.moved) {
        if (
          Math.abs(dx) <= 6 ||
          Math.abs(dx) <= Math.abs(dy)
        ) {
          return;
        }

        this.drag.moved = true;
        this.feed.dataset.dragging = 'true';
      }

      /*
       * Only horizontal mouse dragging is prevented.
       * Vertical wheel scrolling and mobile vertical touch scrolling
       * are NEVER intercepted here.
       */
      event.preventDefault();

      this.feed.scrollLeft =
        this.drag.left - dx;
    }

    pointerUp(event) {
      if (
        !this.drag ||
        event.pointerId !== this.drag.id
      ) {
        return;
      }

      const moved = this.drag.moved;

      try {
        this.feed.releasePointerCapture?.(
          event.pointerId
        );
      } catch (_) {}

      this.drag = null;

      delete this.feed.dataset.dragging;

      if (moved) {
        this.suppressClick = true;

        clearTimeout(
          this.suppressTimer
        );

        this.suppressTimer = setTimeout(
          () => {
            this.suppressClick = false;
          },
          180
        );
      }
    }

    cardStep() {
      const first = this.slides[0];

      if (!first) {
        return this.feed.clientWidth;
      }

      const styles =
        getComputedStyle(this.feed);

      const gap =
        parseFloat(
          styles.columnGap ||
          styles.gap ||
          '0'
        ) || 0;

      return (
        first
          .getBoundingClientRect()
          .width +
        gap
      );
    }

    maxScroll() {
      return Math.max(
        0,
        this.feed.scrollWidth -
          this.feed.clientWidth
      );
    }

    move(direction) {
      const max =
        this.maxScroll();

      if (max <= 2) {
        return;
      }

      const atStart =
        this.feed.scrollLeft <= 2;

      const atEnd =
        this.feed.scrollLeft >=
        max - 2;

      if (
        direction < 0 &&
        atStart
      ) {
        if (this.loop) {
          this.scrollTo(max);
        }

        return;
      }

      if (
        direction > 0 &&
        atEnd
      ) {
        if (this.loop) {
          this.scrollTo(0);
        }

        return;
      }

      const target =
        Math.max(
          0,
          Math.min(
            max,
            this.feed.scrollLeft +
              direction *
                this.cardStep()
          )
        );

      this.scrollTo(target);
    }

    scrollTo(left) {
      this.feed.scrollTo({
        left,
        top: 0,
        behavior:
          reducedMotion.matches
            ? 'auto'
            : 'smooth'
      });
    }

    scrollSlide(slide) {
      if (!slide) return;

      const feedRect =
        this.feed.getBoundingClientRect();

      const slideRect =
        slide.getBoundingClientRect();

      const target =
        this.feed.scrollLeft +
        slideRect.left -
        feedRect.left;

      this.scrollTo(
        Math.max(
          0,
          Math.min(
            this.maxScroll(),
            target
          )
        )
      );
    }

    updateActive(initial = false) {
      const feedRect =
        this.feed.getBoundingClientRect();

      let best =
        this.activeIndex;

      let bestRatio = -1;
      let bestDistance =
        Infinity;

      this.slides.forEach(
        (slide, index) => {
          const rect =
            slide.getBoundingClientRect();

          const visible =
            Math.max(
              0,
              Math.min(
                rect.right,
                feedRect.right
              ) -
                Math.max(
                  rect.left,
                  feedRect.left
                )
            );

          const ratio =
            rect.width
              ? visible /
                rect.width
              : 0;

          const distance =
            Math.abs(
              rect.left -
                feedRect.left
            );

          if (
            ratio >
              bestRatio +
                0.01 ||
            (
              Math.abs(
                ratio -
                  bestRatio
              ) <= 0.01 &&
              distance <
                bestDistance
            )
          ) {
            best = index;
            bestRatio =
              ratio;
            bestDistance =
              distance;
          }
        }
      );

      if (
        bestRatio <= 0 &&
        !initial
      ) {
        return;
      }

      this.setActive(
        best,
        !initial ||
          this.sectionVisible
      );
    }

    setActive(
      index,
      shouldAutoplay = true
    ) {
      if (
        index < 0 ||
        index >=
          this.slides.length
      ) {
        return;
      }

      const changed =
        index !==
        this.activeIndex;

      this.activeIndex =
        index;

      this.slides.forEach(
        (slide, i) => {
          slide.dataset.active =
            i === index
              ? 'true'
              : 'false';

          const video =
            slide.querySelector(
              '[data-svf-video]'
            );

          if (
            changed &&
            video &&
            i !== index
          ) {
            video.pause();
            video.muted = true;

            video.dataset.userPaused =
              'false';

            this.updateMute(
              slide,
              video
            );
          }
        }
      );

      this.updatePagination();
      this.updateNav();

      if (shouldAutoplay) {
        this.syncPlayback();
      }
    }

    ensureLoaded(video) {
      if (
        !video ||
        video.dataset.loaded ===
          'true'
      ) {
        return;
      }

      if (!video.dataset.src) {
        return;
      }

      video.src =
        video.dataset.src;

      video.dataset.loaded =
        'true';

      video.load();
    }

    syncPlayback() {
      if (
        !this.sectionVisible ||
        document.hidden
      ) {
        this.pauseAll();
        return;
      }

      this.slides.forEach(
        (slide, index) => {
          const video =
            slide.querySelector(
              '[data-svf-video]'
            );

          if (
            video &&
            index !==
              this.activeIndex &&
            !video.paused
          ) {
            video.pause();
          }
        }
      );

      const video =
        this.slides[
          this.activeIndex
        ]?.querySelector(
          '[data-svf-video]'
        );

      if (!video) return;

      this.ensureLoaded(video);

      if (
        this.autoplay &&
        video.dataset
          .userPaused !==
          'true' &&
        video.paused
      ) {
        video.muted = true;

        video
          .play()
          .catch(() => {});
      }
    }

    pauseAll() {
      this.slides.forEach(
        (slide) => {
          const video =
            slide.querySelector(
              '[data-svf-video]'
            );

          if (
            video &&
            !video.paused
          ) {
            video.pause();
          }
        }
      );
    }

    toggleVideo(video) {
      const slide =
        video.closest(
          '[data-svf-slide]'
        );

      const index =
        this.slides.indexOf(
          slide
        );

      if (
        index >= 0 &&
        index !==
          this.activeIndex
      ) {
        this.setActive(
          index,
          false
        );
      }

      this.ensureLoaded(video);

      if (video.paused) {
        video.dataset.userPaused =
          'false';

        video
          .play()
          .catch(() => {});
      } else {
        video.dataset.userPaused =
          'true';

        video.pause();
      }
    }

    toggleMute(button) {
      const slide =
        button.closest(
          '[data-svf-slide]'
        );

      const video =
        slide?.querySelector(
          '[data-svf-video]'
        );

      if (!video) return;

      const index =
        this.slides.indexOf(
          slide
        );

      if (
        index >= 0 &&
        index !==
          this.activeIndex
      ) {
        this.setActive(
          index,
          false
        );
      }

      this.ensureLoaded(video);

      video.muted =
        !video.muted;

      this.updateMute(
        slide,
        video
      );

      if (
        !video.muted &&
        video.paused
      ) {
        video.dataset.userPaused =
          'false';

        video
          .play()
          .catch(() => {});
      }
    }

    updateMute(
      slide,
      video
    ) {
      const button =
        slide?.querySelector(
          '[data-svf-mute]'
        );

      if (
        !button ||
        !video
      ) {
        return;
      }

      const soundOn =
        !video.muted;

      button.setAttribute(
        'aria-pressed',
        soundOn
          ? 'true'
          : 'false'
      );

      button.setAttribute(
        'aria-label',
        soundOn
          ? 'Mute video'
          : 'Unmute video'
      );
    }

    updateNav() {
      const max =
        this.maxScroll();

      const overflow =
        max > 2;

      const atStart =
        this.feed.scrollLeft <= 2;

      const atEnd =
        this.feed.scrollLeft >=
        max - 2;

      if (this.prev) {
        this.prev.disabled =
          !overflow ||
          (
            !this.loop &&
            atStart
          );
      }

      if (this.next) {
        this.next.disabled =
          !overflow ||
          (
            !this.loop &&
            atEnd
          );
      }
    }

    updatePagination() {
      const total =
        this.slides.length;

      const current =
        Math.min(
          total,
          this.activeIndex +
            1
        );

      if (this.current) {
        this.current.textContent =
          String(current);
      }

      if (this.total) {
        this.total.textContent =
          String(total);
      }

      if (this.progress) {
        this.progress.style.width =
          `${
            total <= 1
              ? 100
              : (
                  current /
                  total
                ) *
                100
          }%`;
      }
    }

    productData(slide) {
      if (!slide) {
        return null;
      }

      if (
        this.productCache.has(
          slide
        )
      ) {
        return this.productCache.get(
          slide
        );
      }

      const script =
        slide.querySelector(
          '[data-svf-product-json]'
        );

      if (!script) {
        return null;
      }

      try {
        const data =
          JSON.parse(
            script.textContent
          );

        this.productCache.set(
          slide,
          data
        );

        return data;
      } catch (error) {
        console.error(
          'Shoppable Video Feed: invalid product JSON.',
          error
        );

        return null;
      }
    }

    handleAdd(button) {
      if (
        button.dataset.state ===
        'loading'
      ) {
        return;
      }

      const slide =
        button.closest(
          '[data-svf-slide]'
        );

      const product =
        this.productData(
          slide
        );

      if (!product) {
        this.showToast(
          'Unable to load this product.',
          'error'
        );

        return;
      }

      if (
        product.hasVariants
      ) {
        this.openSheet(
          product,
          slide,
          button
        );

        return;
      }

      const variantId =
        Number(
          button.dataset
            .variantId ||
          product.selectedVariantId
        );

      if (!variantId) {
        this.showToast(
          'Please choose an available option.',
          'error'
        );

        return;
      }

      this.addToCart(
        variantId,
        button
      );
    }

    normaliseValue(value) {
      if (
        value == null
      ) {
        return '';
      }

      if (
        typeof value ===
        'object'
      ) {
        return String(
          value.name ??
            value.value ??
            value.title ??
            ''
        );
      }

      return String(value);
    }

    openSheet(
      product,
      slide,
      trigger
    ) {
      if (
        !this.backdrop ||
        !this.sheet ||
        !this.sheetOptions ||
        !this.sheetCta
      ) {
        return;
      }

      const selected =
        product.variants.find(
          (variant) =>
            Number(
              variant.id
            ) ===
            Number(
              product.selectedVariantId
            )
        ) ||
        product.variants.find(
          (variant) =>
            variant.available
        ) ||
        product.variants[0];

      this.sheetState = {
        product,
        slide,
        trigger,
        selectedOptions:
          selected
            ? selected.options.map(
                String
              )
            : [],
        selectedVariant:
          selected || null
      };

      this.lastFocused =
        trigger;

      this.sheetTitle.textContent =
        product.title ||
        'Select options';

      this.renderOptions();
      this.refreshVariant();

      this.backdrop.dataset.open =
        'true';

      this.backdrop.setAttribute(
        'aria-hidden',
        'false'
      );

      requestAnimationFrame(
        () =>
          this.sheet.focus()
      );
    }

    renderOptions() {
      const state =
        this.sheetState;

      if (!state) return;

      this.sheetOptions.replaceChildren();

      state.product.options.forEach(
        (
          option,
          optionIndex
        ) => {
          const fieldset =
            document.createElement(
              'fieldset'
            );

          fieldset.className =
            'svf-option';

          const legend =
            document.createElement(
              'legend'
            );

          legend.className =
            'svf-option__legend';

          legend.textContent =
            option.name ||
            `Option ${
              optionIndex +
              1
            }`;

          fieldset.appendChild(
            legend
          );

          const wrap =
            document.createElement(
              'div'
            );

          wrap.className =
            'svf-option__values';

          (
            Array.isArray(
              option.values
            )
              ? option.values
              : []
          ).forEach(
            (raw) => {
              const value =
                this.normaliseValue(
                  raw
                );

              const button =
                document.createElement(
                  'button'
                );

              button.type =
                'button';

              button.className =
                'svf-option__value';

              button.dataset.svfOptionValue =
                '';

              button.dataset.optionIndex =
                String(
                  optionIndex
                );

              button.dataset.optionValue =
                value;

              button.textContent =
                value;

              const possible =
                state.product.variants.some(
                  (
                    variant
                  ) =>
                    variant.available &&
                    String(
                      variant.options[
                        optionIndex
                      ]
                    ) ===
                      value
                );

              if (!possible) {
                button.disabled =
                  true;

                button.dataset.unavailable =
                  'true';

                button.setAttribute(
                  'aria-disabled',
                  'true'
                );
              }

              wrap.appendChild(
                button
              );
            }
          );

          fieldset.appendChild(
            wrap
          );

          this.sheetOptions.appendChild(
            fieldset
          );
        }
      );

      this.updateOptionButtons();
    }

    selectOption(button) {
      if (
        !this.sheetState ||
        button.disabled
      ) {
        return;
      }

      const index =
        Number(
          button.dataset
            .optionIndex
        );

      if (
        !Number.isInteger(
          index
        )
      ) {
        return;
      }

      this.sheetState.selectedOptions[
        index
      ] =
        button.dataset.optionValue;

      this.updateOptionButtons();
      this.refreshVariant();
    }

    updateOptionButtons() {
      if (
        !this.sheetState
      ) {
        return;
      }

      this.sheetOptions
        .querySelectorAll(
          '[data-svf-option-value]'
        )
        .forEach(
          (button) => {
            const index =
              Number(
                button.dataset
                  .optionIndex
              );

            const selected =
              this.sheetState
                .selectedOptions[
                index
              ] ===
              button.dataset
                .optionValue;

            button.setAttribute(
              'aria-pressed',
              selected
                ? 'true'
                : 'false'
            );
          }
        );
    }

    refreshVariant() {
      if (
        !this.sheetState
      ) {
        return;
      }

      const {
        product,
        selectedOptions,
        slide
      } =
        this.sheetState;

      const variant =
        product.variants.find(
          (
            candidate
          ) =>
            candidate.options.every(
              (
                value,
                index
              ) =>
                String(
                  value
                ) ===
                String(
                  selectedOptions[
                    index
                  ]
                )
            )
        ) || null;

      this.sheetState.selectedVariant =
        variant;

      this.sheetPrice.textContent =
        variant
          ? this.variantPriceLabel(
              variant
            )
          : '';

      this.sheetCta.disabled =
        !(
          variant &&
          variant.available
        );

      this.sheetCta.textContent =
        variant
          ? variant.available
            ? 'Add to Cart'
            : 'Unavailable'
          : 'Select options';

      if (variant) {
        this.updateCardPrice(
          slide,
          variant
        );
      }
    }

    variantPriceLabel(
      variant
    ) {
      if (
        variant.compareAtPrice &&
        Number(
          variant.compareAtPrice
        ) >
          Number(
            variant.price
          )
      ) {
        return `${variant.priceFormatted} · Was ${variant.compareAtPriceFormatted}`;
      }

      return (
        variant.priceFormatted ||
        ''
      );
    }

    updateCardPrice(
      slide,
      variant
    ) {
      const row =
        slide?.querySelector(
          '[data-svf-price-row]'
        );

      if (!row) return;

      const current =
        row.querySelector(
          '[data-svf-price]'
        );

      if (current) {
        current.textContent =
          variant.priceFormatted ||
          '';
      }

      const showCompare =
        row.dataset
          .showComparePrice ===
        'true';

      const hasSale =
        showCompare &&
        Number(
          variant.compareAtPrice
        ) >
          Number(
            variant.price
          );

      let compare =
        row.querySelector(
          '[data-svf-compare-price]'
        );

      if (hasSale) {
        if (!compare) {
          compare =
            document.createElement(
              'span'
            );

          compare.className =
            'svf-price svf-price--compare';

          compare.dataset.svfComparePrice =
            '';

          row.appendChild(
            compare
          );
        }

        compare.textContent =
          variant.compareAtPriceFormatted ||
          '';

        compare.hidden =
          false;
      } else if (compare) {
        compare.hidden =
          true;
      }
    }

    addSelectedVariant() {
      const variant =
        this.sheetState
          ?.selectedVariant;

      if (
        !variant?.available
      ) {
        return;
      }

      this.addToCart(
        variant.id,
        this.sheetCta,
        true
      );
    }

    async addToCart(
      variantId,
      button,
      closeAfter = false
    ) {
      if (
        !variantId ||
        !button ||
        button.dataset.state ===
          'loading'
      ) {
        return;
      }

      const original =
        button.textContent;

      button.dataset.state =
        'loading';

      button.disabled =
        true;

      button.textContent =
        'Adding…';

      try {
        const rootPath =
          window.Shopify
            ?.routes?.root ||
          window.Shopify
            ?.routes
            ?.root_url ||
          '/';

        const endpoint =
          `${
            rootPath.endsWith(
              '/'
            )
              ? rootPath
              : `${rootPath}/`
          }cart/add.js`;

        const response =
          await fetch(
            endpoint,
            {
              method:
                'POST',
              headers: {
                'Content-Type':
                  'application/json',
                Accept:
                  'application/json'
              },
              credentials:
                'same-origin',
              body:
                JSON.stringify(
                  {
                    items:
                      [
                        {
                          id:
                            Number(
                              variantId
                            ),
                          quantity:
                            1
                        }
                      ]
                  }
                )
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.description ||
              data.message ||
              'Unable to add this item to the cart.'
          );
        }

        button.dataset.state =
          'success';

        button.textContent =
          'Added';

        this.showToast(
          'Added to cart',
          'success'
        );

        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles:
                true,
              detail: {
                source:
                  'shoppable-video-feed',
                response:
                  data
              }
            }
          )
        );

        document.dispatchEvent(
          new CustomEvent(
            'cart:updated',
            {
              bubbles:
                true,
              detail: {
                source:
                  'shoppable-video-feed',
                response:
                  data
              }
            }
          )
        );

        this.root.dispatchEvent(
          new CustomEvent(
            'svf:cart:added',
            {
              bubbles:
                true,
              detail: {
                variantId:
                  Number(
                    variantId
                  ),
                response:
                  data
              }
            }
          )
        );

        if (closeAfter) {
          setTimeout(
            () =>
              this.closeSheet(),
            180
          );
        }

        setTimeout(
          () => {
            if (
              !button.isConnected
            ) {
              return;
            }

            button.dataset.state =
              'idle';

            button.disabled =
              false;

            button.textContent =
              button ===
              this.sheetCta
                ? 'Add to Cart'
                : original;
          },
          1200
        );
      } catch (error) {
        button.dataset.state =
          'error';

        button.disabled =
          false;

        button.textContent =
          original;

        this.showToast(
          error?.message ||
            'Unable to add this item to the cart.',
          'error'
        );
      }
    }

    sheetKeydown(event) {
      if (
        !this.backdrop ||
        this.backdrop.dataset.open !==
          'true'
      ) {
        return;
      }

      if (
        event.key ===
        'Escape'
      ) {
        event.preventDefault();
        this.closeSheet();
        return;
      }

      if (
        event.key !==
        'Tab'
      ) {
        return;
      }

      const focusable =
        [
          ...this.sheet.querySelectorAll(
            'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
          )
        ].filter(
          (element) =>
            !element.hidden
        );

      if (!focusable.length) {
        event.preventDefault();
        this.sheet.focus();
        return;
      }

      const first =
        focusable[0];

      const last =
        focusable[
          focusable.length -
            1
        ];

      if (
        event.shiftKey &&
        document.activeElement ===
          first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement ===
          last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    closeSheet() {
      if (
        !this.backdrop ||
        this.backdrop.dataset.open !==
          'true'
      ) {
        return;
      }

      this.backdrop.dataset.open =
        'false';

      this.backdrop.setAttribute(
        'aria-hidden',
        'true'
      );

      const focus =
        this.lastFocused;

      this.lastFocused =
        null;

      this.sheetState =
        null;

      if (
        focus?.isConnected
      ) {
        requestAnimationFrame(
          () =>
            focus.focus()
        );
      }
    }

    showToast(
      message,
      state = 'success'
    ) {
      if (
        !this.toast ||
        !this.toastText
      ) {
        return;
      }

      this.toastText.textContent =
        message;

      this.toast.dataset.state =
        state;

      this.toast.dataset.visible =
        'true';

      this.toast.setAttribute(
        'aria-hidden',
        'false'
      );

      clearTimeout(
        this.toastTimer
      );

      this.toastTimer =
        setTimeout(
          () => {
            this.toast.dataset.visible =
              'false';

            this.toast.setAttribute(
              'aria-hidden',
              'true'
            );
          },
          state ===
            'error'
            ? 5200
            : 3600
        );
    }

    revealBlock(blockId) {
      const slide =
        this.slides.find(
          (item) =>
            item.dataset
              .blockId ===
            String(
              blockId
            )
        );

      if (!slide) {
        return;
      }

      this.scrollSlide(
        slide
      );

      this.setActive(
        this.slides.indexOf(
          slide
        ),
        false
      );
    }
  }

  function init(root) {
    if (
      !root ||
      instances.has(root)
    ) {
      return;
    }

    const instance =
      new ShoppableVideoCarousel(
        root
      );

    instances.set(
      root,
      instance
    );

    instance.init();
  }

  function destroy(root) {
    const instance =
      instances.get(root);

    if (!instance) {
      return;
    }

    instance.destroy();

    instances.delete(
      root
    );
  }

  function rootsInside(
    container
  ) {
    const roots = [];

    if (
      container?.matches?.(
        ROOT
      )
    ) {
      roots.push(
        container
      );
    }

    if (
      container?.querySelectorAll
    ) {
      roots.push(
        ...container.querySelectorAll(
          ROOT
        )
      );
    }

    return roots;
  }

  function boot() {
    document
      .querySelectorAll(
        ROOT
      )
      .forEach(init);
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      rootsInside(
        event.target
      ).forEach(init);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      rootsInside(
        event.target
      ).forEach(
        destroy
      );
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const blockId =
        event.detail
          ?.blockId;

      if (!blockId) {
        return;
      }

      document
        .querySelectorAll(
          ROOT
        )
        .forEach(
          (root) =>
            instances
              .get(root)
              ?.revealBlock(
                blockId
              )
        );
    }
  );
})();